/**
 * Shooting Ranges Service - Strzelnice / miejsca szkoleniowe (słownik)
 * wojskaspecjalne.pl
 */

import { supabase } from './supabase';
import type { Database } from '../types/database.types';

export type ShootingRangeRow = Database['public']['Tables']['shooting_ranges']['Row'];
export type ShootingRange = ShootingRangeRow;

export interface CreateShootingRangeData {
  name: string;
  slug?: string | null;
  description?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  coordinates?: { lat: number; lng: number } | null;
  website_url?: string | null;
  phone?: string | null;
  email?: string | null;
  is_active?: boolean;
  display_order?: number;
}

export interface UpdateShootingRangeData extends Partial<CreateShootingRangeData> {
  id: string;
}

export async function getAllShootingRanges(options?: {
  includeInactive?: boolean;
  sortByOrder?: boolean;
}): Promise<{ success: boolean; data?: ShootingRange[]; error?: string }> {
  try {
    let query = supabase
      .from('shooting_ranges')
      .select('*');

    if (!options?.includeInactive) {
      query = query.eq('is_active', true);
    }

    if (options?.sortByOrder) {
      query = query.order('display_order', { ascending: true }).order('name', { ascending: true });
    } else {
      query = query.order('name', { ascending: true });
    }

    const { data, error } = await query;
    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    const code = error?.code as string | undefined;
    const message = (error?.message as string | undefined) || 'Unknown error';

    // Helpful guidance when the table hasn't been deployed yet (PostgREST schema cache)
    if (
      code === 'PGRST205' ||
      message.includes("Could not find the table 'public.shooting_ranges'") ||
      message.includes('schema cache')
    ) {
      return {
        success: false,
        error:
          "Brak tabeli 'public.shooting_ranges' w Supabase (migracja nie wdrożona lub złe środowisko). " +
          "Wdróż migrację `supabase/migrations/20260120_create_shooting_ranges.sql` do tej instancji Supabase, a potem odśwież stronę.",
      };
    }

    console.error('Error fetching shooting ranges:', error);
    return { success: false, error: message };
  }
}

export async function createShootingRange(
  payload: CreateShootingRangeData
): Promise<{ success: boolean; data?: ShootingRange; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('shooting_ranges')
      .insert({
        name: payload.name.trim(),
        slug: payload.slug?.trim() || null,
        description: payload.description?.trim() || null,
        address: payload.address?.trim() || null,
        city: payload.city?.trim() || null,
        country: payload.country?.trim() || 'Poland',
        coordinates: payload.coordinates ?? null,
        website_url: payload.website_url?.trim() || null,
        phone: payload.phone?.trim() || null,
        email: payload.email?.trim() || null,
        is_active: payload.is_active ?? true,
        display_order: payload.display_order ?? 0,
      })
      .select('*')
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error: any) {
    console.error('Error creating shooting range:', error);
    return { success: false, error: error.message };
  }
}

export async function updateShootingRange(
  payload: UpdateShootingRangeData
): Promise<{ success: boolean; data?: ShootingRange; error?: string }> {
  try {
    const { id, ...fields } = payload;
    const updateFields: Partial<ShootingRangeRow> = {};

    if (fields.name !== undefined) updateFields.name = fields.name.trim();
    if (fields.slug !== undefined) updateFields.slug = fields.slug?.trim() || null;
    if (fields.description !== undefined) updateFields.description = fields.description?.trim() || null;
    if (fields.address !== undefined) updateFields.address = fields.address?.trim() || null;
    if (fields.city !== undefined) updateFields.city = fields.city?.trim() || null;
    if (fields.country !== undefined) updateFields.country = fields.country?.trim() || null;
    if (fields.coordinates !== undefined) updateFields.coordinates = fields.coordinates;
    if (fields.website_url !== undefined) updateFields.website_url = fields.website_url?.trim() || null;
    if (fields.phone !== undefined) updateFields.phone = fields.phone?.trim() || null;
    if (fields.email !== undefined) updateFields.email = fields.email?.trim() || null;
    if (fields.is_active !== undefined) updateFields.is_active = fields.is_active;
    if (fields.display_order !== undefined) updateFields.display_order = fields.display_order;

    const { data, error } = await supabase
      .from('shooting_ranges')
      .update(updateFields)
      .eq('id', id)
      .select('*')
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error: any) {
    console.error('Error updating shooting range:', error);
    return { success: false, error: error.message };
  }
}


